"use client";

import { FormInput } from "../forms/form-input";
import { Form } from "../ui/form";
import { Button } from "../ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useSignUp, useSignupEmail } from "../lib/hooks/use-auth";
import { FaUser } from "react-icons/fa";
import { PiBankFill } from "react-icons/pi";
import { cn } from "../lib/utils";
import { FormSelect } from "../forms/form-select";
import { COUNTRY_OPTIONS } from "../lib/constants";
import { useRouter } from "next/navigation";
import { GoogleAuthButton } from "./google-auth-button";
import { PasswordInput } from "@padi/xui/password-input";
import { phoneDataSchema } from "../ui/phone-input";

// ================================
// ✅ Schema
// ================================
const formSchema = z
  .object({
    email: z.email({ message: "Invalid email address" }),
    firstName: z.string().min(1, { message: "First name is required" }),
    lastName: z.string().min(1, { message: "Last name is required" }),
    password: z.string().superRefine((val, ctx) => {
      const hasNumber = /[0-9]/.test(val);
      const hasUpper = /[A-Z]/.test(val);
      const hasLower = /[a-z]/.test(val);
      const hasLength = val.length >= 8;

      if (!hasNumber || !hasUpper || !hasLower || !hasLength) {
        ctx.addIssue({
          code: "custom",
          message: "Please meet all password requirements",
        });
      }
    }),
    confirmPassword: z.string(),
    organisationType: z.enum(["individual", "company"], {
      message: "Please select an organisation type",
    }),
    organisationName: z.string().optional(),
    countryCode: z.string().min(1, { message: "Country is required" }),
    phoneNumber: phoneDataSchema,
  })
  .refine(
    (data) => data.organisationType === "individual" || !!data.organisationName,
    {
      message: "Organisation name is required for companies",
      path: ["organisationName"],
    },
  )
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

// ================================
// ✅ Types
// ================================
type SignupFormType = z.infer<typeof formSchema>;

// ================================
// ✅ Component
// ================================
export function SignUpForm({ platform }: { platform: string }) {
  const { mutate, isPending } = useSignUp({ platform });
  const { email } = useSignupEmail();
  const router = useRouter();

  const orgTypes = [
    {
      id: "individual" as const,
      title: "Individual",
      description: "Sign up as an individual user or freelancer.",
      icon: FaUser,
    },
    {
      id: "company" as const,
      title: "Company",
      description: "Sign up on behalf of a company or organisation.",
      icon: PiBankFill,
    },
  ];

  const form = useForm<SignupFormType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: email || "",
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      organisationType: "individual",
      organisationName: "",
      countryCode: "",
      phoneNumber: "",
    },
    mode: "onChange",
  });

  const orgType = form.watch("organisationType");

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => mutate(data))}
        className="grid gap-4"
      >
        {/* Organisation Type Selection */}
        <div className="flex flex-col sm:flex-row gap-x-6 mb-6">
          {orgTypes.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => form.setValue("organisationType", type.id)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                orgType === type.id ? "bg-[#E8F4F2]" : " hover:bg-gray-50",
              )}
            >
              <div className="p-3 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <type.icon className="size-6 fill-secondary" />
              </div>
              <div className="text-left">
                <h3 className="font-bold text-sm text-foreground">
                  {type.title}
                </h3>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {type.description}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-3">
          <FormInput
            control={form.control}
            name="firstName"
            placeholder="First Name"
          />
          <FormInput
            control={form.control}
            name="lastName"
            placeholder="Last Name"
          />
        </div>

        {/* Email */}
        <div className="relative">
          <FormInput
            control={form.control}
            name="email"
            label="Email address"
            placeholder="Email"
            disabled
          />
          <button
            type="button"
            onClick={() => router.push("/signin")}
            className="absolute right-0 top-0 text-secondary text-xs font-bold hover:underline h-6 flex items-center"
          >
            Change
          </button>
        </div>

        {/* Country */}
        <FormSelect
          name="countryCode"
          placeholder="Country"
          options={COUNTRY_OPTIONS}
          control={form.control}
        />

        {/* Phone Number */}
        <FormInput
          control={form.control}
          name="phoneNumber"
          type="phone"
          label="Phone Number"
          placeholder="Enter phone number"
        />

        {/* Organisation Name (only when company selected) */}
        {orgType === "company" && (
          <FormInput
            control={form.control}
            name="organisationName"
            placeholder="Organisation Name"
          />
        )}

        {/* Passwords */}
        <div className="grid md:grid-cols-2 gap-3">
          <PasswordInput
            control={form.control}
            name="password"
            placeholder="Password"
          />
          <PasswordInput
            control={form.control}
            name="confirmPassword"
            placeholder="Confirm Password"
            hideReqs
          />
        </div>

        {/* Submit */}
        <Button
          type="submit"
          variant="secondary"
          className="w-full mt-6 md:mt-8"
          disabled={isPending}
        >
          {isPending ? "Signing up..." : "Continue"}
        </Button>

        <div className="relative flex items-center py-2">
          <div className="flex-grow border-t border-gray-200"></div>
          <span className="flex-shrink mx-4 text-gray-400 text-xs font-semibold">
            OR
          </span>
          <div className="flex-grow border-t border-gray-200"></div>
        </div>

        <GoogleAuthButton platform={platform} />
      </form>
    </Form>
  );
}
